// Help text for: Tools > Environment (Live, Historical, Efficiency).
// Keys are referenced as "environment.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  // ---------- Live ----------
  sensorName: {
    title: "Name",
    use: "The room or spot where this temperature and humidity sensor is fitted.",
    source: "setting",
    how: "The name given to the sensor when it was fitted; you can change it in Info → Node Settings. Every sensor set up as a room sensor shows up in this list.",
  },
  liveTime: {
    title: "Time",
    use: "When this sensor last sent a reading (Pakistan time).",
    source: "backend",
    how: "The time stamp on the newest reading held for that sensor. If nothing has come in for more than 30 minutes, or the sensor has never reported at all, it counts as offline and this reads \"More than 30 mins\" in red.",
  },
  liveTemperature: {
    title: "Temperature (°C)",
    use: "How warm that room is right now, coloured against the room's limits.",
    source: "measured",
    how: "Read from the sensor in that room, in °C to 1 decimal place, updated every 10 seconds. Red = above the upper limit or below the lower limit; amber (Near Limit) = still inside that band but at or past an amber limit; green = in range; no colour = no limits set for the room in Info → Node Settings. Shows - when the sensor is offline.",
  },
  liveHumidity: {
    title: "Humidity (%)",
    use: "How damp the air in that room is right now.",
    source: "measured",
    how: "Read from the sensor in that room as % relative humidity, to 1 decimal place, updated every 10 seconds. Shows - when the sensor has sent nothing for more than 30 minutes.",
  },
  liveAvgTemperature: {
    title: "Avg Temperature (°C)",
    use: "The room's average temperature so far today, so you can judge the whole day rather than one moment.",
    source: "calculated",
    how: "All of today's readings from that room's sensor averaged, counting from midnight Pakistan time, in °C to 1 decimal place. Coloured by the same limit rules as Temperature: red past the upper or lower limit, amber at an amber limit, green in range.",
  },
  liveAvgHumidity: {
    title: "Avg Humidity (%)",
    use: "The room's average humidity so far today.",
    source: "calculated",
    how: "All of today's humidity readings from that room's sensor averaged, counting from midnight Pakistan time, in % to 1 decimal place.",
  },
  liveStatus: {
    title: "Status",
    use: "Whether the sensor is still sending readings.",
    source: "backend",
    how: "ON when the newest reading is 30 minutes old or less. OFF when it is older than that, or when the sensor has never reported.",
  },
  liveMeterStatus: {
    title: "Meter Status",
    use: "Whether the sensor is still in touch with EnViz.",
    source: "backend",
    how: "online when a reading arrived in the last 30 minutes, offline if not — the same 30-minute rule as Status.",
  },

  // ---------- Historical ----------
  historicalDateRange: {
    title: "From / To",
    use: "The dates and times whose readings are drawn on the chart and scored in the Baseline QCP list.",
    source: "input",
    how: "Comes pre-filled with the last 7 full days ending yesterday (00:00 to 23:59:59). 1D / 3D / 7D / 30D fill the boxes with that many full days ending yesterday. Nothing redraws until you press Apply.",
  },
  historicalSensor: {
    title: "Sensor",
    use: "The main room sensor drawn on the chart and heatmap.",
    source: "input",
    how: "Comes pre-filled with the first room in the list, in name order. Your choice takes effect when you press Apply; you can add more rooms using the buttons under the chart.",
  },
  historicalMetric: {
    title: "Temp / Humidity",
    use: "Picks whether the chart shows temperature in °C or humidity in %.",
    source: "input",
    how: "Comes pre-filled as Temp (°C). The same sensor measures both, and your choice takes effect when you press Apply.",
  },
  historicalView: {
    title: "Analysis / Heatmap",
    use: "Analysis draws readings over time. Heatmap shows the usual reading for each day of the week and hour of the day.",
    source: "input",
    how: "Each heatmap square is the average of every hourly average that falls on that weekday and hour inside your dates, for one room only. The heatmap always works in whole hours.",
  },
  historicalAggregation: {
    title: "Minute / Hour / Day",
    use: "How closely readings are grouped along the chart.",
    source: "backend",
    how: "Each point is the average of every reading in that minute, hour or day (Pakistan time), to 2 decimal places. Comes pre-filled as Hour and reloads the chart as soon as you change it; the heatmap is fixed to Hour.",
  },
  historicalSelectedSensors: {
    title: "Selected Sensors",
    use: "How many rooms are drawn on the chart. Click a name to add or drop one.",
    source: "input",
    how: "The room picked in the drop-down always stays on the chart and cannot be dropped. With only one room on a line chart, dashed Max, Average and Min lines are drawn from the points on screen.",
  },

  // ---------- Efficiency ----------
  efficiencyDateRange: {
    title: "From / To",
    use: "The days over which each room's temperature is scored.",
    source: "input",
    how: "Comes pre-filled with the last 7 full days ending yesterday, both days counted in full. 1D / 3D / 7D / 30D fill the boxes; press Apply to reload.",
  },
  overallCompliance: {
    title: "Overall Compliance",
    use: "How much of the time all your monitored rooms held their temperature inside their limits.",
    source: "calculated",
    how: "Across every room with readings: those inside the lower-to-upper limit band, divided by all readings, times 100. A reading sitting exactly on a limit counts as in range, and rooms that report more often count for more. Green from 90% up, amber 50–89%, red below 50%.",
  },
  roomsMonitored: {
    title: "Rooms Monitored",
    use: "How many rooms are being scored.",
    source: "calculated",
    how: "Counts the temperature sensors that have a Standard, an upper limit and a lower limit all set in Info → Node Settings and sent at least one reading in the chosen days.",
  },
  roomsOutOfRange: {
    title: "Rooms Out of Range",
    use: "How many rooms strayed outside their limits at least once in the chosen days.",
    source: "calculated",
    how: "Counts the monitored rooms scoring under 100%, meaning at least one reading went above the upper limit or below the lower limit.",
  },
  worstRoom: {
    title: "Worst Room",
    use: "The room that held its temperature band least well, so you know where to look first.",
    source: "calculated",
    how: "The monitored room with the lowest score for the chosen days. Its score is shown underneath.",
  },
  room: {
    title: "Room",
    use: "The room being scored. The worst room is listed first.",
    source: "setting",
    how: "The sensor name set in Info → Node Settings. Only rooms with a Standard, an upper limit and a lower limit all set appear here; rooms that sent no readings in the chosen days come last, marked -.",
  },
  target: {
    title: "Target (°C)",
    use: "The temperature this room is meant to be held at.",
    source: "setting",
    how: "The Standard temperature you set for that room's sensor in Info → Node Settings, in °C.",
  },
  avgTemp: {
    title: "Avg Temp (°C)",
    use: "The room's average temperature over the chosen days.",
    source: "calculated",
    how: "Every reading from that room's sensor in the chosen days, averaged, in °C to 1 decimal place.",
  },
  avgDeviation: {
    title: "Avg Deviation (°C)",
    use: "How far the room drifted from its target on average, whether too warm or too cold.",
    source: "calculated",
    how: "The gap between each reading and the Target, counted without a plus or minus sign, then averaged — in °C to 2 decimal places. 0 means the room sat right on target.",
  },
  compliance: {
    title: "Compliance %",
    use: "The share of the time this room stayed inside its limits.",
    source: "calculated",
    how: "Readings inside the lower-to-upper limit band, divided by all its readings in the chosen days, times 100. Amber limits are not counted here. Green from 90% up, amber 50–89%, red below 50%.",
  },
  hoursOutOfRange: {
    title: "Hours Out of Range",
    use: "Roughly how many hours the room spent outside its limits.",
    source: "calculated",
    how: "100% minus Compliance %, times the length of the chosen days in hours, treating readings as evenly spread. Hours when the sensor was quiet are shared out in the same proportion rather than left out.",
  },
  dailyCompliance: {
    title: "Daily Compliance by Room",
    use: "Shows how each room's score moved day by day, so you can spot a bad day or a room that is drifting.",
    source: "calculated",
    how: "For each room and each calendar day: readings inside the limit band, divided by that day's readings, times 100. A break in a line means that room sent no readings that day.",
  },
};

export default help;
